import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../../../util/AppContext';
import PropType from 'prop-types';
import { getConfigValue } from '../../../util/getConfigValue';
import { downloadDivChart, getTimePeriodLabel, utilizationReportConstants, standardizeChartNames, standardizeChartDataLabels, sortDoughnutChartData, getTimePeriodTile } from './utlizationChart';
import SimpleDoughnutChart from './SimpleDoughnutChart';
import { getQuicksheetsByActionType } from '../../../API/UtilizationReportingDashboard';
import { useErrorBoundary } from "react-error-boundary";
import ReportInfoTooltip from "../ReportInfoTooltip";
import Button from 'react-bootstrap/Button';
import Mustache from 'mustache';
import { formatNoDataMessage } from "../../../util/formatNoDataMessage";
import { createColorPaletteRegistry } from '../ColorRegistry';


QuicksheetsByActionTypeChart.propTypes = {
    filterState: PropType.object,
    colorRegistry: PropType.object,
    productData: PropType.shape({
            name: PropType.string,
            chartData: PropType.shape({
                labels: PropType.arrayOf(PropType.string),
                dataSets: PropType.arrayOf(PropType.object)
            })
        })
}

function QuicksheetsByActionTypeChart(props) {
    const {
        filterState,
        colorRegistry
    } = props;

    const { showBoundary } = useErrorBoundary();
    const [appState] = useContext(AppContext);
    const doughnutChartRefs = useRef([]);
    const [data, setData] = useState({});
    const [doughnutChartData, setDoughnutChartData] = useState({});
    const [isLoading, setIsLoading] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeButton, setActiveButton] = useState(utilizationReportConstants.current);
    const tooltipTarget = useRef(null);
    const chartEnabled = JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfActionForQuicksheets?.Enabled, appState)?.Value === 'true') || false;
    const [noData, setNoData] = useState(null);
    const [chartColorRegistry] = useState(() => createColorPaletteRegistry(
        JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.ColorPalette, appState)?.Value) || [],
        { storageKey: 'utilizationReportingDashboard:quicksheets:colorpalette' }
    ));

    const getData = async () => {
        if (!filterState?.selectedProductIds) {
            return;
        }

        setIsLoading(true);
        await getQuicksheetsByActionType({
            "timeFilter": filterState.timePeriodId,
            "productIds": filterState.selectedProductIds,
            "includePriorPeriod": true
        })
            .then((responseJson) => {
                setData(responseJson);
                setChartData(responseJson.currentPeriod);
            })
            .catch((err) => {
                showBoundary(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const toggleChartTab = (activeTab, selectedData) => {
        setChartData(selectedData);
        setActiveButton(activeTab);
    }

    const setChartData = (selectedData) => {
        chartColorRegistry.reset();
        let filteredDataSet = filterState.allProducts
            ? selectedData.filter(p => !p.chartData.dataSets.some(d => d.label !== utilizationReportConstants.allProducts))
            : selectedData.filter(p => !p.chartData.dataSets.some(d => d.label === utilizationReportConstants.allProducts));

        setNoData(filteredDataSet.filter(p => p.chartData.labels.includes(utilizationReportConstants.no_data)));
        filteredDataSet = filteredDataSet.filter(p => !p.chartData.labels.includes(utilizationReportConstants.no_data));

        let standardizedData = standardizeChartNames(filteredDataSet, appState);
        standardizedData = standardizeChartDataLabels(standardizedData, appState);

        const data = standardizedData.map(item => {
            const sortedChartData = sortDoughnutChartData(item.chartData);
            return {
                ...item,
                chartData: {
                    ...sortedChartData,
                    dataSets: sortedChartData.dataSets.map(ds =>
                        addColorPalette(ds, sortedChartData.labels),
                    )
                }
            };
        });

        setDoughnutChartData(data);

    }

    const addColorPalette = (selectedDataSet, labels = []) => {
        const assignedColorPallet = labels.map(key => chartColorRegistry.getColorForKey(key));
        return {
            ...selectedDataSet,
            backgroundColor: assignedColorPallet
        };
    }

    useEffect(() => {
        if (chartEnabled && appState?.config) {
            getData();
            setActiveButton(utilizationReportConstants.current);
        }
    }, [filterState.selectedProductIds, filterState.timePeriodId]);

    let content;
    if (isLoading === null || isLoading) {
        content = (<div className='p-5'>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</div>);
    }
    else {
        content = (
            <>
                <div id="pdfGenerationsByMessageType-ChartsArea">
                    <div className='p-4 chart-label'>
                        <div>{getTimePeriodLabel(filterState.timePeriodId, activeButton, false)}</div>
                        <div>{filterState.allProducts ? Mustache.render(getConfigValue(appState?.config?.UtilizationDashboard?.Filters?.Product?.AllProducts?.Label, appState)?.Value || null, { count: appState?.sellSheetProductDetails?.length }) : filterState.selectedProductIds?.map(id => {
                            const product = appState?.sellSheetProductDetails.find(p => p.clientProductId === id);
                            return product.name || `Unknow (${id})`;
                        }).join(', ')}</div>
                    </div>
                    <div className='container-fluid'>
                        {Array.from({ length: Math.ceil(doughnutChartData.length / 2) }, (_, i) => {
                            return (<div className='row mb-5 justify-content-center align-items-center' key={i}>
                                {doughnutChartData.slice(i * 2, (i + 1) * 2).map((product, columnIndex) => {
                                    const refIndex = i * 2 + columnIndex;
                                    if (!doughnutChartRefs?.current[refIndex]) {
                                        doughnutChartRefs.current[refIndex] = React.createRef();
                                    }
                                    return (
                                        <SimpleDoughnutChart
                                            ref={doughnutChartRefs.current[refIndex]}
                                            columnSize = {6}
                                            productData={product}
                                            appState={appState}
                                            key={refIndex} />
                                    )
                                })}
                            </div>)
                        })}
                    </div>
                    <div className='p-4 chart-label'>
                        {formatNoDataMessage(noData)}
                    </div>
                </div>
                <div className='reportButtons-container'>

                    <Button
                        className='reportTextButton'
                        onClick={() => downloadDivChart('pdfGenerationsByMessageType-ChartsArea', getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfActionForQuicksheets?.Title, appState)?.Value || null)}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Text, appState)?.Value || null} <i className={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Icon, appState)?.Value || null}></i>
                    </Button>
                </div>
                <div className='reportTabButton-container'>
                    <Button
                        className={`tab-button ${activeButton === utilizationReportConstants.current ? "active" : "inactive"}`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.current, data.currentPeriod); }}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.CurrentSelection?.Text, appState)?.Value || null} ({getTimePeriodTile(filterState.timePeriodId, filterState.timePeriodTitle, false)})
                    </Button>
                    <Button
                        className={`tab-button ${activeButton === utilizationReportConstants.prior ? "active" : "inactive"}`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.prior, data.priorPeriod); }}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Text, appState)?.Value || null}
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            {chartEnabled &&
                <div className='chart-panel'>
                    <div className='chart-header'>
                        <Button className={`chart-header-button ${isCollapsed ? 'collapsed' : ''}`} onClick={() => { setIsCollapsed(!isCollapsed) }}>
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfActionForQuicksheets?.Title, appState)?.Value || "Product by Message Types"}
                            <ReportInfoTooltip ref={tooltipTarget} text={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfActionForQuicksheets?.ToolTip, appState)?.Value} />
                        </Button>
                    </div>
                    <div className={`chart-container ${isCollapsed ? 'collapsed' : ''}`}>
                        {content}
                    </div>
                </div>
            }
        </>
    );
}

export default QuicksheetsByActionTypeChart;